/**
 * Import members — the four-step stepper.
 *
 * The file is read in the browser and never leaves it until the owner has
 * seen the preview: mapping columns and spotting a bad row are decisions a
 * person makes, and uploading first would mean half a roster lands before
 * anyone has looked at it.
 *
 * Spec: DESIGN-BRIEF.md §D and `vImport()` in prototype.html.
 */
import React, {useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ArrowDownTrayIcon} from '@heroicons/react/24/outline';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import type {NetlyPayData} from '../../../internals/managers/NetlyPayManager';
import {
  IMPORT_FIELDS,
  IMPORT_STEPS,
  importPlan,
  importSummary,
  mappingState,
  parseCsv,
  previewRows,
  suggestMapping,
  templateCsv,
  type FileColumn,
} from '../importMembers';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {ErrorState, Skeleton} from '../components/States';
import {GhostButton, PrimaryButton} from '../components/Overlays';
import {ScreenGate, Toast} from '../components/ScreenGate';

const th = 'px-3 py-2.5 text-left text-[12px] font-semibold text-text3 whitespace-nowrap';
const td = 'px-3 py-2.5 text-[13px] text-text1 align-middle whitespace-nowrap';

export const ImportMembersView = ({selectedShop}: any) => {
  const shopId = selectedShop?.shopId;
  const link = useNetlyPayLink(shopId);
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState<FileColumn[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [map, setMap] = useState<Record<string, string>>({});
  const [skipErrors, setSkipErrors] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<{added: number; skipped: number} | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const roster = useNetlyPayFetch<NetlyPayData>(
    link.phase === 'linked' ? shopId : null,
    async (id) => {
      const people = await NetlyPayRepository.listMembers(id);
      return {
        members: people?.members ?? [],
        programs: people?.programs ?? [],
        batches: people?.batches ?? [],
        requests: [],
      };
    },
    [link.phase],
  );

  const data: NetlyPayData = roster.data ?? {members: [], programs: [], batches: [], requests: []};

  const mapping = useMemo(() => mappingState(columns, map), [columns, map]);
  const preview = useMemo(() => previewRows(rows, map, data), [rows, map, data]);
  const plan = useMemo(() => importPlan(preview, skipErrors), [preview, skipErrors]);

  const readFile = (file: File) => {
    if (!/\.(csv|txt)$/i.test(file.name)) {
      // Excel needs a parser the dashboard does not bundle; see the note below.
      flash('Save the sheet as CSV and try again');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      if (!parsed.rows.length) {
        flash('That file has headings but no rows');
        return;
      }
      setFileName(file.name);
      setColumns(parsed.columns);
      setRows(parsed.rows);
      setMap(suggestMapping(parsed.columns));
      setStep(2);
    };
    reader.onerror = () => flash('Could not read that file');
    reader.readAsText(file);
  };

  const download = () => {
    const blob = new Blob([templateCsv(data.batches[0]?.name ?? 'U12')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'netly-pay-members-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const run = async () => {
    if (!shopId || plan.blocked) return;
    setRunning(true);
    setStep(4);
    try {
      const res = await NetlyPayRepository.importMembers(shopId, {
        rows: skipErrors ? rows.filter((_, i) => !preview[i].error) : rows,
        mapping: map,
        skipErrors,
      });
      setDone({added: res?.added ?? plan.ready, skipped: res?.skipped?.length ?? plan.errors});
    } catch (e: any) {
      flash(e?.message ?? 'The import did not finish');
      setStep(3);
    } finally {
      setRunning(false);
    }
  };

  const restart = () => {
    setStep(1);
    setFileName('');
    setColumns([]);
    setRows([]);
    setMap({});
    setDone(null);
    setSkipErrors(true);
  };

  return (
    <ScreenGate link={link} shopId={shopId} shop={selectedShop}>
      <div className="flex flex-col w-full p-7 gap-5 nl-rise">
        <div>
          <h1 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] leading-tight">
            Import members
          </h1>
          <p className="text-[13px] text-text2 mt-1">
            Bring your list in from a spreadsheet. Nothing is saved until you have seen the preview.
          </p>
        </div>

        {/* Stepper */}
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {IMPORT_STEPS.map((s, i) => {
            const n = i + 1;
            const complete = n < step || (n === 4 && !!done);
            const current = n === step;
            return (
              <li
                key={s.label}
                aria-current={current ? 'step' : undefined}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                  current ? 'border-brand bg-brand-soft/30' : 'border-borderc bg-surface'
                }`}
              >
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${
                    complete
                      ? 'bg-pos text-white'
                      : current
                      ? 'bg-brand text-white'
                      : 'bg-surface3 text-text3'
                  }`}
                >
                  {complete ? '✓' : n}
                </span>
                <span className="min-w-0">
                  <span
                    className={`block text-[13.5px] font-semibold ${current ? 'text-brand' : 'text-text1'}`}
                  >
                    {s.label}
                  </span>
                  <span className="block text-[11.5px] text-text3 truncate">{s.sub}</span>
                </span>
              </li>
            );
          })}
        </ol>

        {roster.phase === 'loading' && (
          <div className="bg-surface border border-borderc rounded-card">
            <Skeleton rows={4} />
          </div>
        )}

        {roster.phase === 'error' && (
          <div className="bg-surface border border-borderc rounded-card">
            <ErrorState message={roster.error} onRetry={roster.reload} />
          </div>
        )}

        {roster.phase !== 'loading' && roster.phase !== 'error' && (
          <div className="bg-surface border border-borderc rounded-card p-6">
            {/* 1 · Upload */}
            {step === 1 && (
              <div className="flex flex-col gap-4 max-w-[620px]">
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) readFile(file);
                  }}
                  onClick={() => fileInput.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
                  }}
                  className={`rounded-xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors ${
                    dragging ? 'border-brand bg-brand-soft/30' : 'border-borderc2 bg-surface2'
                  }`}
                >
                  <p className="text-[14.5px] font-semibold text-text1">
                    Drop a CSV or Excel file here
                  </p>
                  <p className="text-[12.5px] text-text2 mt-1.5">
                    or click to choose · up to 2,000 rows
                  </p>
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".csv,.txt,.xlsx,.xls"
                    aria-label="Choose a file to import"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) readFile(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <GhostButton onClick={download}>
                    <span className="flex items-center gap-1.5">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      Download the template
                    </span>
                  </GhostButton>
                  <p className="text-[12px] text-text3">
                    Full name, phone and batch are required. Everything else is optional.
                  </p>
                </div>
              </div>
            )}

            {/* 2 · Map columns */}
            {step === 2 && (
              <div className="flex flex-col gap-4">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 className="text-[16px] font-semibold text-text1">
                    Match your headings to member fields
                  </h2>
                  <p className="text-[12.5px] text-text3">
                    {fileName} · {rows.length} {rows.length === 1 ? 'row' : 'rows'}
                  </p>
                </div>

                <div className="overflow-x-auto border border-borderc rounded-xl">
                  <table className="w-full border-collapse">
                    <thead className="bg-surface2">
                      <tr>
                        <th className={th}>YOUR COLUMN</th>
                        <th className={th}>FIRST ROW</th>
                        <th className={th}>IMPORT AS</th>
                        <th className={th}>&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mapping.rows.map((r) => (
                        <tr key={r.column} className="border-t border-borderc">
                          <td className={`${td} font-medium`}>{r.column}</td>
                          <td className={`${td} text-text3 font-mono text-[12px]`}>{r.sample || '—'}</td>
                          <td className={td}>
                            <select
                              value={r.field}
                              onChange={(e) => setMap({...map, [r.column]: e.target.value})}
                              aria-label={`Import ${r.column} as`}
                              className={`nl-select nl-input--sm w-auto ${
                                r.usedTwice ? 'border-neg' : 'border-borderc2'
                              }`}
                            >
                              {IMPORT_FIELDS.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td
                            className={`${td} text-[12px] font-medium ${
                              r.tone === 'danger' ? 'text-neg' : r.tone === 'muted' ? 'text-text3' : 'text-pos'
                            }`}
                          >
                            {r.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!mapping.canContinue && (
                  <p className="text-[12.5px] text-neg">
                    {mapping.missingText ||
                      'Two columns are set to the same field. Change one of them, or skip it.'}
                  </p>
                )}

                <div className="flex items-center gap-2.5">
                  <GhostButton onClick={() => setStep(1)}>Back</GhostButton>
                  <PrimaryButton disabled={!mapping.canContinue} onClick={() => setStep(3)}>
                    Preview {rows.length} {rows.length === 1 ? 'row' : 'rows'}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* 3 · Preview */}
            {step === 3 && (
              <div className="flex flex-col gap-4">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 className="text-[16px] font-semibold text-text1">Check each row</h2>
                  <p className="text-[12.5px] text-text3 font-mono tabular-nums">
                    {plan.ready} ready · {plan.errors} with errors
                  </p>
                </div>

                <div className="overflow-x-auto border border-borderc rounded-xl max-h-[420px]">
                  <table className="w-full border-collapse">
                    <thead className="bg-surface2 sticky top-0">
                      <tr>
                        <th className={`${th} w-10`}>#</th>
                        <th className={th}>NAME</th>
                        <th className={th}>PHONE</th>
                        <th className={th}>GUARDIAN</th>
                        <th className={th}>BATCH</th>
                        <th className={th}>DATE OF BIRTH</th>
                        <th className={th}>CHECK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r) => (
                        <tr
                          key={r.n}
                          className={`border-t border-borderc ${r.error ? 'bg-neg-soft/40' : ''}`}
                        >
                          <td className={`${td} text-text3 font-mono`}>{r.n}</td>
                          <td className={`${td} font-medium`}>{r.name || '—'}</td>
                          <td
                            className={`${td} font-mono text-[12px] ${
                              r.error === 'phone' || r.error === 'dup'
                                ? 'text-neg font-semibold'
                                : 'text-text2'
                            }`}
                          >
                            {r.phone}
                          </td>
                          <td className={`${td} text-text2`}>{r.guardian || '—'}</td>
                          <td
                            className={`${td} ${r.error === 'batch' ? 'text-neg font-semibold' : 'text-text2'}`}
                          >
                            {r.batch}
                          </td>
                          <td className={`${td} font-mono text-[12px] text-text2`}>{r.dob || '—'}</td>
                          <td
                            className={`${td} text-[12px] ${
                              r.error ? 'text-neg font-semibold' : 'text-pos font-medium'
                            }`}
                          >
                            {r.check}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {plan.errors > 0 && (
                  <label className="flex items-start gap-3 px-4 py-3 rounded-xl border border-borderc bg-surface2 cursor-pointer max-w-[560px]">
                    <input
                      type="checkbox"
                      checked={skipErrors}
                      onChange={() => setSkipErrors(!skipErrors)}
                      className="w-4 h-4 mt-0.5 accent-[var(--brand)] cursor-pointer"
                    />
                    <span>
                      <span className="block text-[13px] font-semibold text-text1">
                        Skip rows with errors
                      </span>
                      <span className="block text-[12.5px] text-text2">{plan.note}</span>
                    </span>
                  </label>
                )}
                {plan.errors === 0 && <p className="text-[12.5px] text-text2">{plan.note}</p>}

                <div className="flex items-center gap-2.5">
                  <GhostButton onClick={() => setStep(2)}>Back</GhostButton>
                  <PrimaryButton disabled={plan.blocked} onClick={run}>
                    Import {plan.ready} {plan.ready === 1 ? 'member' : 'members'}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* 4 · Import */}
            {step === 4 && (
              <div className="flex flex-col gap-4 max-w-[560px]">
                <h2 className="text-[16px] font-semibold text-text1">
                  {done ? 'Import complete' : 'Importing members'}
                </h2>
                <div className="h-2 rounded-full bg-surface3 overflow-hidden">
                  <div
                    className="h-full bg-brand rounded-full transition-all duration-500"
                    style={{width: done ? '100%' : '65%'}}
                  />
                </div>
                {done ? (
                  <>
                    <p className="text-[13.5px] text-text1 font-medium">
                      {importSummary({...plan, ready: done.added, errors: done.skipped}, skipErrors)}
                    </p>
                    <div className="flex items-center gap-2.5">
                      <PrimaryButton onClick={() => navigate('/members')}>See the members</PrimaryButton>
                      <GhostButton onClick={restart}>Import another file</GhostButton>
                    </div>
                  </>
                ) : (
                  <p className="text-[13px] text-text2">
                    {running ? 'Saving the rows to your member list.' : 'Starting…'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {toast && <Toast message={toast} />}
      </div>
    </ScreenGate>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(ImportMembersView);
